import { LOCALSTORAGE_ACTIVE_ORG_ID_KEY } from "constants/index";
import { safeLocalStorageSetItem } from "utils/localStorage";
import { UserContext } from "contexts/UserContext";
import Organization from "../entities/Organization.entity";
import type IOrg from "models/Organization.model";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import {
  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
} from "server/devplane/organizations/mutations";
import { useListOrganizationsQuery } from "server/devplane/organizations/queries";
import type { OrganizationAccess } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_access_pb";
import { orgContextDefaultValues } from "tests/defaultValues";

import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import { OrgRoleIds } from "models/Role.model";
import { usePerformanceCookieConsent } from "utils/oneTrustCookieConsent";
import { getOrganizationRoleForOrg } from "utils/organizationAccess";
import {
  refreshCurrentUserQuery,
  useCurrentUserQuery,
} from "server/devplane/users/queries";
import { SYSTEM_SUPERUSER_ROLE_ID } from "server/devplane/users/api";
import { useOrganizationSetup } from "./useOrganizationSetup";

export interface IOrgContext {
  orgs: Organization[];
  activeOrgId: string;
  activeOrg: Organization | null;
  activeOrgRole: OrgRoleIds | undefined;
  isLoading: boolean;
  isOrgAccessLoading: boolean;
  isOrgAccessError: boolean;
  setActiveOrgId: (id: string) => void;
  refreshOrgs: () => Promise<Organization[]>;
  refreshOrgAccesses: () => Promise<OrganizationAccess[]>;
  createOrSyncOrg: (name: string) => Promise<IOrg>;
  deleteOrg: (id: string) => Promise<string>;
}

export const OrgContext = React.createContext<IOrgContext>(
  orgContextDefaultValues,
);

const emptyOrganizationAccesses: OrganizationAccess[] = [];

function resolveActiveOrgId({
  organizationListEnabled,
  availableOrgIds,
  routeOrgId,
  currentActiveOrgId,
  cachedActiveOrgId,
}: {
  organizationListEnabled: boolean;
  availableOrgIds: string[];
  routeOrgId?: string;
  currentActiveOrgId: string;
  cachedActiveOrgId: string | null;
}): string {
  if (!organizationListEnabled || availableOrgIds.length === 0) {
    return "";
  }
  if (routeOrgId && availableOrgIds.includes(routeOrgId)) {
    return routeOrgId;
  }
  if (availableOrgIds.includes(currentActiveOrgId)) {
    return currentActiveOrgId;
  }
  if (cachedActiveOrgId && availableOrgIds.includes(cachedActiveOrgId)) {
    return cachedActiveOrgId;
  }
  return availableOrgIds[0];
}

const OrgContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { isAuthenticated, me } = useContext(UserContext);
  const [selectedActiveOrgId, setActiveOrgId] = useState("");

  const organizationListEnabled = isAuthenticated && Boolean(me?.id);
  const {
    data: currentUser,
    isFetching: isOrgAccessLoading,
    isError: isOrgAccessError,
  } = useCurrentUserQuery(isAuthenticated);
  const currentUserOrganizationAccesses =
    currentUser?.organizationAccesses ?? emptyOrganizationAccesses;
  const refreshOrgAccesses = useCallback(async (): Promise<
    OrganizationAccess[]
  > => {
    if (!isAuthenticated) {
      return [];
    }

    try {
      const refreshedCurrentUser = await refreshCurrentUserQuery(queryClient);
      return refreshedCurrentUser?.organizationAccesses ?? [];
    } catch {
      return [];
    }
  }, [isAuthenticated, queryClient]);
  const isSuperAdmin =
    currentUser?.roles.some(
      (role) => role.roleId === SYSTEM_SUPERUSER_ROLE_ID,
    ) ?? false;
  const {
    data: organizationData,
    error: organizationError,
    isError: isOrganizationError,
    isLoading: isOrganizationsLoading,
    refetch: refetchOrganizations,
  } = useListOrganizationsQuery(me?.id ?? "", organizationListEnabled);
  const { mutateAsync: createOrganization } = useCreateOrganizationMutation();
  const { mutateAsync: deleteOrganization } = useDeleteOrganizationMutation();
  const orgs = useMemo(
    () =>
      organizationListEnabled
        ? (organizationData ?? []).map((org) => new Organization(org))
        : [],
    [organizationData, organizationListEnabled],
  );
  const activeOrgId =
    orgs.some((org) => org.id === selectedActiveOrgId) ||
    (currentUser?.userId === me?.id &&
      currentUserOrganizationAccesses.some(
        (access) => access.organizationId === selectedActiveOrgId,
      ))
      ? selectedActiveOrgId
      : "";
  // Org-scoped routes have a page after the ID; /org/environments and other
  // orgless routes use their second segment as the page instead.
  const routeOrgId = matchPath<{ orgId: string }>(location.pathname, {
    path: "/org/:orgId/:page",
  })?.params.orgId;
  const hasPerformanceCookieConsent = usePerformanceCookieConsent();
  const activeOrg = orgs.find((org) => org.id === activeOrgId) || null;
  const activeOrgRole = getOrganizationRoleForOrg(
    currentUserOrganizationAccesses,
    activeOrgId,
  );

  const history = useHistory();

  const createOrSyncOrg = useCallback(
    async (name: string): Promise<IOrg> => {
      const organization = await createOrganization(name);
      if (!organization) {
        throw new Error("The organization was created without a response.");
      }
      if (orgs.length === 0) {
        setActiveOrgId(organization.id);
      }
      await refreshOrgAccesses();
      return organization;
    },
    [createOrganization, orgs.length, refreshOrgAccesses],
  );

  const deleteOrg = useCallback(
    async (id: string): Promise<string> => {
      if (orgs.length <= 1)
        throw new Error("Must have at least one organization.");
      const remainingOrgs = orgs.filter((org) => org.id !== id);
      const nextActiveOrgId =
        activeOrgId === id ? (remainingOrgs[0]?.id ?? "") : activeOrgId;
      await deleteOrganization(id);
      setActiveOrgId(nextActiveOrgId);
      return nextActiveOrgId;
    },
    [activeOrgId, deleteOrganization, orgs],
  );

  const preferredActiveOrgId = resolveActiveOrgId({
    organizationListEnabled,
    availableOrgIds: orgs.map((org) => org.id),
    routeOrgId,
    currentActiveOrgId: selectedActiveOrgId,
    cachedActiveOrgId: localStorage.getItem(LOCALSTORAGE_ACTIVE_ORG_ID_KEY),
  });

  useEffect(() => {
    if (activeOrgId) {
      // register() adds activeOrgId to every analytics event.
      if (hasPerformanceCookieConsent) {
        posthog?.register({ activeOrgId: activeOrgId });
      }

      // Persist the active organization as required functional state for
      // PostHog's user- and organization-targeted feature flags.
      posthog?.people.set({ activeOrgId });

      safeLocalStorageSetItem(LOCALSTORAGE_ACTIVE_ORG_ID_KEY, activeOrgId);
    }
  }, [activeOrgId, hasPerformanceCookieConsent]);

  useEffect(() => {
    setActiveOrgId(preferredActiveOrgId);
  }, [preferredActiveOrgId]);

  // If the route org id is not the active org id, redirect to the active org id
  useEffect(() => {
    if (
      !routeOrgId ||
      !activeOrg ||
      isOrganizationsLoading ||
      isOrgAccessLoading ||
      // Allow platform admins to view any org
      isSuperAdmin ||
      orgs.some((org) => org.id === routeOrgId)
    ) {
      return;
    }

    history.replace({
      pathname: location.pathname.replace(
        /^\/org\/[^/]+/,
        `/org/${activeOrgId}`,
      ),
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
  }, [
    activeOrg,
    activeOrgId,
    history,
    isOrgAccessLoading,
    isSuperAdmin,
    isOrganizationsLoading,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    orgs,
    routeOrgId,
  ]);

  const refreshOrgs = useCallback(async (): Promise<Organization[]> => {
    const result = await refetchOrganizations();
    if (result.error) {
      throw result.error;
    }
    return (result.data ?? []).map((org) => new Organization(org));
  }, [refetchOrganizations]);

  const hasCompletedOrganizationSetup = useOrganizationSetup({
    enabled: organizationListEnabled,
    isOrganizationsLoading,
    hasOrganizationData: Boolean(organizationData),
    isOrganizationError,
    organizationError,
    organizations: orgs,
    initialActiveOrgId: preferredActiveOrgId,
    user: me,
    createOrSyncOrg,
  });
  const isLoading =
    organizationListEnabled &&
    (isOrganizationsLoading || !hasCompletedOrganizationSetup);

  const providerData = useMemo(
    () => ({
      orgs,
      activeOrgId,
      activeOrg,
      activeOrgRole,
      isLoading,
      isOrgAccessLoading,
      isOrgAccessError,
      setActiveOrgId,
      refreshOrgs,
      refreshOrgAccesses,
      createOrSyncOrg,
      deleteOrg,
    }),
    [
      activeOrg,
      activeOrgId,
      activeOrgRole,
      createOrSyncOrg,
      deleteOrg,
      isLoading,
      isOrgAccessError,
      isOrgAccessLoading,
      orgs,
      refreshOrgAccesses,
      refreshOrgs,
    ],
  );

  return (
    <OrgContext.Provider value={providerData}>{children}</OrgContext.Provider>
  );
};

export default OrgContextProvider;
